import React, { useState, useEffect } from 'react';
import { Plus, Grid, List, Instagram, Linkedin, X, User, Calendar, Phone, Mail, Camera } from 'lucide-react';
import './TeamMembers.css';
import Dashboard from './Dashboard';
import { useCompany } from '../context/CompanyContext';

const TeamMembers = () => {
  const { selectedCompany } = useCompany();
  
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    profile_picture_url: '',
    bio: '',
    user_type: 'platform_user',
    dob: '',
    gender: 'male'
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [createPostError, setCreatePostError] = useState('');

  const fetchUsers = async () => {
    setLoading(true);
    
    const token = localStorage.getItem('token');
    
    if (!token) {
      setLoading(false);
      return [];
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/list-company-users/${selectedCompany.company_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
        console.log(data);
      } else {
        console.error('Error fetching skills:', response.statusText);
      }
    } catch (err) {
      console.error('Error fetching skills:', err);
    } finally {
      setLoading(false);
    }
  };

    useEffect(() => {
      fetchUsers();
    }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!formData.first_name) newErrors.first_name = 'First name is required';
    if (!formData.last_name) newErrors.last_name = 'Last name is required';

    if (formData.phone_number && !/^\+?[\d\s-()]+$/.test(formData.phone_number)) {
      newErrors.phone_number = 'Please enter a valid phone number';
    }

    if (formData.profile_picture_url && !/^https?:\/\/.+/.test(formData.profile_picture_url)) {
      newErrors.profile_picture_url = 'Please enter a valid URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    setCreatePostError('');

    try {
      // Simulate API call
      console.log('Form submitted:', formData);

      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/user-company-signup/${selectedCompany.company_id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      if (response.ok) {

        setFormData({
            email: '',
            password: '',
            first_name: '',
            last_name: '',
            phone_number: '',
            profile_picture_url: '',
            bio: '',
            user_type: "platform_company",
            dob: '',
            gender: ''
        });
      } else {
        const errorData = await response.json();
        setCreatePostError(errorData.detail || 'Failed to create post');
      }
      // Reset form and close modal
      setShowAddModal(false);
    } catch (error) {
      setCreatePostError('Failed to create post');
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setErrors({});
    setFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      phone_number: '',
      profile_picture_url: '',
      bio: '',
      user_type: "platform_company",
      dob: '',
      gender: ''
    });
  };

  return (
    <Dashboard page="teams">
    <div className="team-members-container">
      <div className="team-members-max-width">
        {/* Header */}
        <div className="team-members-header">
          <h1 className="team-members-title">Settings</h1>

          {createPostError && (
              <div className="error-message">{createPostError}</div>
          )}
          
          {/* Basic Information Section */}
          {/* <div className="team-members-basic-info">
            <h2 className="team-members-subtitle">Basic Information</h2>
            <p className="team-members-description">Add team members of your company</p>
          </div> */}
        </div>

        {/* Team Members Section */}
        <div className="team-members-card">
          {/* Section Header */}
          <div className="team-members-card-header">
            <h3 className="team-members-members-title">50 Members</h3>
            <div className="team-members-header-actions">
              <button
                onClick={() => setShowAddModal(true)}
                className="team-members-add-button"
              >
                <Plus size={20} />
                Add Members
              </button>
              <div className="team-members-view-toggle">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`team-members-view-button ${viewMode === 'grid' ? 'active' : ''}`}
                >
                  <Grid size={18} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`team-members-view-button ${viewMode === 'list' ? 'active' : ''}`}
                >
                  <List size={18} />
                </button>
              </div>
            </div>
          </div>

          {/* Team Members Grid */}
          <div className="team-members-card-content">
            <div className="team-members-grid">
              {users.map((member) => (
                <div key={member.id} className="team-members-member-card">
                  <img
                    src={`${process.env.REACT_APP_API_URL}/profile/get-pic/${encodeURIComponent(member.id)}`}
                    alt={member.first_name}
                    className="team-members-avatar"
                  />
                  <h4 className="team-members-member-name">
                    {member.first_name} {member.last_name}
                  </h4>
                  <p className="team-members-member-title">
                      <span className={member.is_active ? "status-badge status-live" : "status-draft"} >
                        {member.is_active ? "Enabled" : "Disabled"}
                      </span>
                  </p>
                  <p className="team-members-member-title">
                    {member.email}
                  </p>
                  <p className="team-members-member-title">
                    Last Seen {formatDate(member.last_time_online)}
                  </p>
                  {/* <div className="team-members-social-links">
                    <a
                      href={member.socialLinks.instagram}
                      className="team-members-social-link"
                    >
                      <Instagram size={20} />
                    </a>
                    <a
                      href={member.socialLinks.linkedin}
                      className="team-members-social-link"
                    >
                      <Linkedin size={20} />
                    </a>
                  </div> */}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Add Member Modal */}
        {showAddModal && (
          <div className="team-members-modal-overlay" onClick={handleCloseModal}>
            <div className="team-members-modal" onClick={(e) => e.stopPropagation()}>
              {/* Modal Header */}
              <div className="team-members-modal-header">
                <h3 className="team-members-modal-title">Add Team Member</h3>
                <button
                  onClick={handleCloseModal}
                  className="team-members-close-button"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Modal Content */}
              <div className="team-members-modal-content">
                {/* Personal Information */}
                <div className="team-members-form-group">
                  <div className="team-members-form-row">
                    <div>
                      <label className="team-members-label">
                        <span className="team-members-label-icon">
                          <User size={16} />
                          First Name *
                        </span>
                      </label>
                      <input
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleInputChange}
                        className={`team-members-input ${errors.first_name ? 'error' : ''}`}
                        placeholder="John"
                      />
                      {errors.first_name && (
                        <p className="team-members-error-text">{errors.first_name}</p>
                      )}
                    </div>

                    <div>
                      <label className="team-members-label">Last Name *</label>
                      <input
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleInputChange}
                        className={`team-members-input ${errors.last_name ? 'error' : ''}`}
                        placeholder="Doe"
                      />
                      {errors.last_name && (
                        <p className="team-members-error-text">{errors.last_name}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="team-members-form-group">
                  <label className="team-members-label">
                    <span className="team-members-label-icon">
                      <Mail size={16} />
                      Email *
                    </span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`team-members-input ${errors.email ? 'error' : ''}`}
                    placeholder="john@example.com"
                  />
                  {errors.email && (
                    <p className="team-members-error-text">{errors.email}</p>
                  )}
                </div>

                <div className="team-members-form-group">
                  <label className="team-members-label">Password *</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`team-members-input ${errors.password ? 'error' : ''}`}
                    placeholder="••••••••"
                  />
                  {errors.password && (
                    <p className="team-members-error-text">{errors.password}</p>
                  )}
                </div>

                <div className="team-members-form-group">
                  <label className="team-members-label">
                    <span className="team-members-label-icon">
                      <Phone size={16} />
                      Phone Number
                    </span>
                  </label>
                  <input
                    type="tel"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    className={`team-members-input ${errors.phone_number ? 'error' : ''}`}
                    placeholder="+1 234 567 8900"
                  />
                  {errors.phone_number && (
                    <p className="team-members-error-text">{errors.phone_number}</p>
                  )}
                </div>

                <div className="team-members-form-group">
                  <label className="team-members-label">Bio</label>
                  <select
                        value={formData.gender}
                        name="gender"
                        className="team-members-select"
                        onChange={(e) => handleInputChange}
                    >
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                  </select>
                </div>

                <div className="team-members-form-group">
                  <label className="team-members-label">
                    <span className="team-members-label-icon">
                      <Calendar size={16} />
                      Date of Birth
                    </span>
                  </label>
                  <input
                    type="date"
                    name="dob"
                    value={formData.dob}
                    onChange={handleInputChange}
                    className="team-members-input"
                  />
                </div>

                <div className="team-members-form-group">
                  <label className="team-members-label">
                    <span className="team-members-label-icon">
                      <Camera size={16} />
                      Profile Picture URL
                    </span>
                  </label>
                  <input
                    type="url"
                    name="profile_picture_url"
                    value={formData.profile_picture_url}
                    onChange={handleInputChange}
                    className={`team-members-input ${errors.profile_picture_url ? 'error' : ''}`}
                    placeholder="https://example.com/profile/image.jpg"
                  />
                  {errors.profile_picture_url && (
                    <p className="team-members-error-text">{errors.profile_picture_url}</p>
                  )}
                </div>

                <div className="team-members-form-group">
                  <label className="team-members-label">Bio</label>
                  <textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    rows={4}
                    className="team-members-textarea"
                    placeholder="Tell us about this team member..."
                  />
                </div>

                {/* Modal Footer */}
                <div className="team-members-modal-footer">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="team-members-cancel-button"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="team-members-submit-button"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="team-members-spinner"></div>
                        Adding...
                      </>
                    ) : (
                      'Add Member'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </Dashboard>
  )};
  export default TeamMembers;